import { useMutation, useQuery } from "@apollo/client";
import { useState, useEffect } from "react";
import { Box } from "@mui/system";
import { useSelectedBusiness } from "../../../redux/store";
import { GET_ALL_COUNTRIES } from "../../../graphql/queries/countryStateCity";
import { GET_HOLIDAYS_BY_COUNTRY } from "../../../graphql/queries/holiday";  
import { GET_BUSINESS_HOLIDAYS } from "../../../graphql/queries/businessHolidays";
import { ADD_BUSINESS_HOLIDAY, ADD_BUSINESS_HOLIDAYS_IN_BULK, REMOVE_BUSINESS_HOLIDAY } from "../../../graphql/mutations/businessHoliday";

const Holiday = () => {
    const selectedBusiness = useSelectedBusiness();
    const businessId = Number(selectedBusiness?.businessBasic?.id);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [existingHolidays, setExistingHolidays] = useState([]);
    const [message, setMessage] = useState("");
    const [isCountrySelected, setIsCountrySelected] = useState(false);

    // Fetch the list of countries
    const { data: countriesData, loading: countriesLoading, error: countriesError } = useQuery(GET_ALL_COUNTRIES);

    // Fetch already saved holidays for the business
    const { data: businessHolidaysData, loadingoading: businessHolidaysLoading, error: businessHolidaysError, refetch: refetchBusinessHolidays } = useQuery(GET_BUSINESS_HOLIDAYS, {
        variables: { businessId },
        onCompleted: (data) => {
            if (data.getBusinessHolidays.length > 0) {
                const holidayIds = data.getBusinessHolidays.map((holiday) => holiday.holidayId.toString());
                setExistingHolidays(holidayIds);
                const associatedCountryId = data.getBusinessHolidays[0].holiday.countryId;
                setSelectedCountry(associatedCountryId);
                setIsCountrySelected(true);
            }
        },
    });

    const { data: holidaysData, loading: holidaysLoading, error: holidaysError } = useQuery(
        GET_HOLIDAYS_BY_COUNTRY,
        {
            variables: { countryIds: selectedCountry ? [parseInt(selectedCountry)] : [] }, // Wrap the single country ID in an array
            skip: !selectedCountry, // Skip if no country is selected
        }
    );



    const [addBusinessHoliday] = useMutation(ADD_BUSINESS_HOLIDAY, {
        onCompleted: () => {
            refetchBusinessHolidays(); // Refetch after adding holiday
        },
    });

    const [removeBusinessHoliday] = useMutation(REMOVE_BUSINESS_HOLIDAY, {
        onCompleted: () => {
            refetchBusinessHolidays(); // Refetch after removing holiday
        },
    });

    // New Mutation for adding holidays in bulk
    const [addBusinessHolidaysInBulk] = useMutation(ADD_BUSINESS_HOLIDAYS_IN_BULK, {
        onCompleted: () => {
            refetchBusinessHolidays(); // Refetch after bulk add
        },
        onError: (error) => {
            console.error("Error adding holidays:", error); // Handle error gracefully
        },
    });

    // New function to add holidays in bulk
    const handleBulkHolidayAdd = async () => {
        if (!selectedCountry || !businessId) {
            setMessage("No holidays data available, country not selected, or business ID missing.");
            return;
        }

        try {
            const isSameCountry = businessHolidaysData?.getBusinessHolidays?.some(
                businessHoliday => businessHoliday.holiday.countryId === selectedCountry
            );

            if (isSameCountry) {
                return; // Exit early if the country is the same
            }
            const { data } = await addBusinessHolidaysInBulk({
                variables: { businessId, countryId: selectedCountry }, // Pass countryId and businessId
            });

            if (data && data.addBusinessHolidaysInBulk) {
                const addedHolidayIds = data.addBusinessHolidaysInBulk.map(holiday => holiday.holidayId.toString());

                setMessage(data.message || "Holidays added successfully");

                // Update the existing holidays list
                setExistingHolidays(addedHolidayIds);
            } else {
                setMessage("Failed to add holidays in bulk.");
            }
        } catch (err) {
            console.error(err); // Log full error for debugging
            setMessage(`Error: ${err.message}`);
        }
    };

    // Trigger the bulk add whenever the country is selected
    useEffect(() => {
        if (selectedCountry) {
            handleBulkHolidayAdd(); // Automatically call the bulk add when a country is selected
        }
    }, [selectedCountry]);


    if (businessHolidaysLoading) return <p>Loading holidays...</p>;
    if (businessHolidaysError) return <p>Error loading holidays: {businessHolidaysError.message}</p>;
    if (holidaysLoading) return <p>Loading holidays...</p>;
    if (holidaysError) return <p>Error loading holidays: {holidaysError.message}</p>;


    const handleCountryChange = (event) => {
        const selected = event.target.value;
        setSelectedCountry(Number(selected)); // Keep as a string for `<select>`
        setIsCountrySelected(true);
    };

    const handleHolidayChange = async (event) => {
        const { value, checked } = event.target;
        const holidayId = parseInt(value); // Convert the value to integer

        if (!holidayId) {
            setMessage("Holiday ID is invalid.");
            return;
        }

        try {
            if (checked) {
                // Add holiday
                const { data } = await addBusinessHoliday({
                    variables: { businessId, holidayId },
                });

                if (data) {
                    setMessage(data.message || "Holiday added successfully");
                    setExistingHolidays((prevState) => [...prevState, holidayId.toString()]);
                } else {
                    setMessage("Failed to add the holiday.");
                }
            } else {
                // Remove holiday
                const { data } = await removeBusinessHoliday({
                    variables: { businessId, holidayId },
                });

                if (data?.removeBusinessHoliday?.success) {
                    setMessage(data.removeBusinessHoliday.message || "Holiday removed successfully");
                    setExistingHolidays((prevState) => prevState.filter(id => id !== holidayId.toString()));
                } else {
                    setMessage("Failed to remove the holiday.");
                }
            }
        } catch (err) {
            console.error(err); // Log full error for debugging
            setMessage(`Error: ${err.message}`);
        }
    };

    if (countriesLoading) return <p>Loading countries...</p>;
    if (countriesError) return <p>Error loading countries: {countriesError.message}</p>;

    return (
        <div>
            {/* Country Selection Message */}
            <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                    Plany365 automatically removes your availability on selected holidays based on your country.
                    Adjust your preferences to control which holidays apply.
                </h3>
            </div>

            {/* Country Selection */}
            {isCountrySelected ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', mt: "20px", mb: "20px" }}>
                    <p style={{ margin: 0 }}>
                        {countriesData.getAllCountries.find(
                            (country) => Number(country.id) === Number(selectedCountry)
                        )?.name || "Unknown Country"}
                    </p>
                    <button onClick={() => setIsCountrySelected(false)}>Change Country</button>
                </Box>

            ) : (
                <>
                    <label htmlFor="country-select">Select a Country:</label>
                    <select id="country-select" value={selectedCountry || ""} onChange={handleCountryChange}>
                        <option value="" disabled>
                            -- Select a Country --
                        </option>
                        {countriesData.getAllCountries.map((country) => (
                            <option key={country.id} value={country.id}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </>
            )}

            {/* Holidays List */}
            {holidaysLoading && <p>Loading holidays...</p>}
            {holidaysError && <p>Error loading holidays: {holidaysError.message}</p>}

            {selectedCountry && holidaysData && (
                <div>
                    {holidaysData.getHolidaysByCountry.map((holiday) => (
                        <div key={holiday.id}>
                            <input
                                type="checkbox"
                                value={holiday.id}
                                id={`holiday-${holiday.id}`}
                                onChange={handleHolidayChange}
                                checked={existingHolidays.includes(holiday.id.toString())} // Ensure comparing as strings
                            />
                            <label htmlFor={`holiday-${holiday.id}`}>{holiday.holidayName}</label>
                        </div>
                    ))}
                </div>
            )}

            {message && <p>{message}</p>}
        </div>
    );
};

export default Holiday;
